import React from 'react';
import { Pressable, StyleSheet, Text, View } from 'react-native';

import { useImportOnboarding } from '../../contexts/ImportOnboardingContext';
import { useSampleData } from '../../contexts/SampleDataContext';
import {
  usePortfolioAccountEditor,
  type PortfolioListCategory,
} from '../../hooks/usePortfolioAccountEditor';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { formatCurrency } from '../charts/d3/format';
import { ErrorRetry } from '../ui/ErrorRetry';
import { GlassButton } from '../ui/GlassButton';
import { GlassCard } from '../ui/GlassCard';
import { RefreshButton } from '../ui/RefreshButton';
import { EditPortfolioAccountModal } from './EditPortfolioAccountModal';

export type PortfolioFallbackRow = {
  id: string;
  label: string;
  value: number;
  expectedReturnPct?: number;
};

type Props = {
  title?: string;
  /** Active investments (default) vs Other assets bucket. */
  category?: PortfolioListCategory;
  /** Read-only rows when Mongo snapshot is empty (sample / offline). */
  fallbackRows?: PortfolioFallbackRow[];
  /** Also show fallback rows under synced rows (e.g. local home/car). */
  includeFallbackWhenSynced?: boolean;
  /** Optional header link (e.g. open Wealth). */
  headerAction?: { label: string; onPress: () => void };
  /** Compact spacing for Home widget. */
  compact?: boolean;
};

/**
 * Shared Home + Wealth portfolio list — import, manual add, edit / delete.
 */
export function PortfolioAccountsCard({
  title = 'Your accounts',
  category = 'investment',
  fallbackRows = [],
  includeFallbackWhenSynced = false,
  headerAction,
  compact = false,
}: Props) {
  const { openImportMore } = useImportOnboarding();
  const { isSampleMode } = useSampleData();
  const {
    categoryAccounts,
    totalBalance,
    mutating,
    error,
    hasSnapshot,
    editing,
    adding,
    modalVisible,
    categoryIsInvestment,
    openEdit,
    openAdd,
    closeEdit,
    saveEdit,
    requestDelete,
  } = usePortfolioAccountEditor({ category });

  const useSynced = hasSnapshot;
  const syncedRows = useSynced
    ? categoryAccounts.map(({ account, index, isOwn }) => ({
        key: account.accountId ?? `synced_${index}_${account.label}`,
        index,
        label: account.label,
        value: account.balance,
        expectedReturnPct: account.expectedReturnPct,
        hiddenFromHousehold: account.hiddenFromHousehold === true,
        // Partner rows are visible in Household view but not editable.
        editable: isOwn,
      }))
    : [];
  const fallbackDisplay = (!useSynced || includeFallbackWhenSynced
    ? fallbackRows
    : []
  ).map((row) => ({
    key: row.id,
    index: -1,
    label: row.label,
    value: row.value,
    expectedReturnPct: row.expectedReturnPct,
    editable: false as const,
  }));

  const rows = useSynced ? [...syncedRows, ...fallbackDisplay] : fallbackDisplay;

  const addLabel = categoryIsInvestment ? '+ Add' : '+ Add asset';
  const emptyHint = isSampleMode
    ? categoryIsInvestment
      ? 'Sample investment accounts — sign in to import your brokerage.'
      : 'Sample assets — sign in to add home, cash, or other holdings.'
    : categoryIsInvestment
      ? 'Import a screenshot or add an account manually. Edits sync across devices.'
      : 'Add home, cash, or other non-invested assets. Syncs across devices and Trends.';

  const addActions = isSampleMode ? null : (
    <View style={styles.ctaRow}>
      <GlassButton
        variant="primary"
        size="sm"
        onPress={openAdd}
        disabled={mutating}
        accessibilityLabel={
          categoryIsInvestment
            ? 'Add portfolio account manually'
            : 'Add other asset manually'
        }
      >
        {addLabel}
      </GlassButton>
      {categoryIsInvestment ? (
        <GlassButton
          variant="ghost"
          size="sm"
          onPress={openImportMore}
          disabled={mutating}
          accessibilityLabel="Import portfolio screenshots"
        >
          Import screenshots
        </GlassButton>
      ) : null}
    </View>
  );

  if (rows.length === 0) {
    return (
      <>
        <GlassCard style={[styles.card, compact && styles.cardCompact]}>
          <View style={styles.header}>
            <View style={styles.headerText}>
              <Text style={styles.title}>{title}</Text>
              <Text style={styles.hint}>{emptyHint}</Text>
            </View>
            <View style={styles.headerActions}>
              {headerAction ? (
                <GlassButton
                  variant="ghost"
                  size="sm"
                  onPress={headerAction.onPress}
                  accessibilityLabel={headerAction.label}
                >
                  {headerAction.label}
                </GlassButton>
              ) : null}
              <RefreshButton compact />
            </View>
          </View>
          {error ? <ErrorRetry message={error} compact /> : null}
          {addActions}
        </GlassCard>
        <EditPortfolioAccountModal
          visible={modalVisible}
          initial={editing}
          mode={adding ? 'add' : 'edit'}
          category={category}
          saving={mutating}
          onClose={closeEdit}
          onSave={(draft) => {
            void saveEdit(draft).catch(() => {
              // error via context
            });
          }}
        />
      </>
    );
  }

  const principal =
    useSynced && syncedRows.length > 0
      ? totalBalance
      : rows.reduce((sum, r) => sum + r.value, 0);

  return (
    <>
      <GlassCard style={[styles.card, compact && styles.cardCompact]}>
        <View style={styles.header}>
          <View style={styles.headerText}>
            <Text style={styles.title}>{title}</Text>
            <Text style={styles.hint}>
              {formatCurrency(principal)} total
              {useSynced && syncedRows.length > 0 && !isSampleMode
                ? ' · tap icons to edit'
                : ''}
            </Text>
          </View>
          <View style={styles.headerActions}>
            {!isSampleMode ? (
              <GlassButton
                variant="ghost"
                size="sm"
                onPress={openAdd}
                disabled={mutating}
                accessibilityLabel="Add account"
              >
                +
              </GlassButton>
            ) : null}
            {headerAction ? (
              <GlassButton
                variant="ghost"
                size="sm"
                onPress={headerAction.onPress}
                accessibilityLabel={headerAction.label}
              >
                {headerAction.label}
              </GlassButton>
            ) : null}
            <RefreshButton compact />
          </View>
        </View>

        {error ? <ErrorRetry message={error} compact /> : null}

        <View style={styles.list}>
          {rows.map((row) => (
            <View key={row.key} style={styles.row}>
              <View style={styles.meta}>
                <View style={styles.labelRow}>
                  <Text style={styles.label} numberOfLines={1}>
                    {row.label}
                  </Text>
                  {'hiddenFromHousehold' in row && row.hiddenFromHousehold ? (
                    <View
                      style={styles.personalChip}
                      accessibilityLabel="Personal — hidden from household"
                    >
                      <Text style={styles.personalChipMark}>✓</Text>
                      <Text style={styles.personalChipText}>Personal</Text>
                    </View>
                  ) : null}
                </View>
                {row.expectedReturnPct != null ? (
                  <Text style={styles.returnLabel}>
                    {row.expectedReturnPct > 0 ? '+' : ''}
                    {row.expectedReturnPct}%
                  </Text>
                ) : null}
              </View>
              <Text style={styles.value}>{formatCurrency(row.value)}</Text>
              {row.editable && !isSampleMode ? (
                <View style={styles.actions}>
                  <Pressable
                    onPress={() => openEdit(row.index)}
                    disabled={mutating}
                    hitSlop={10}
                    style={({ pressed }) => [
                      styles.iconBtn,
                      pressed && styles.iconBtnPressed,
                      mutating && styles.iconBtnDisabled,
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={`Edit ${row.label}`}
                  >
                    <Text style={styles.iconGlyph}>✎</Text>
                  </Pressable>
                  <Pressable
                    onPress={() => {
                      void requestDelete(row.index);
                    }}
                    disabled={mutating}
                    hitSlop={10}
                    style={({ pressed }) => [
                      styles.iconBtn,
                      styles.deleteBtn,
                      pressed && styles.iconBtnPressed,
                      mutating && styles.iconBtnDisabled,
                    ]}
                    accessibilityRole="button"
                    accessibilityLabel={`Delete ${row.label}`}
                  >
                    <Text style={[styles.iconGlyph, styles.deleteText]}>×</Text>
                  </Pressable>
                </View>
              ) : null}
            </View>
          ))}
        </View>

        {addActions}
      </GlassCard>

      <EditPortfolioAccountModal
        visible={modalVisible}
        initial={editing}
        mode={adding ? 'add' : 'edit'}
        category={category}
        saving={mutating}
        onClose={closeEdit}
        onSave={(draft) => {
          void saveEdit(draft).catch(() => {
            // error via context
          });
        }}
      />
    </>
  );
}

const styles = StyleSheet.create({
  card: {
    gap: spacing.sm,
  },
  cardCompact: {
    gap: spacing.xs,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.sm,
  },
  headerText: {
    flex: 1,
    gap: 2,
  },
  headerActions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
  },
  error: {
    ...typography.caption,
    color: colors.danger,
  },
  list: {
    gap: 0,
    marginTop: spacing.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    paddingVertical: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: colors.glassBorder,
  },
  meta: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  labelRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.sm,
    minWidth: 0,
  },
  label: {
    ...typography.body,
    color: colors.text,
    fontWeight: '600',
    flexShrink: 1,
  },
  personalChip: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: radii.pill,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: 'rgba(61, 214, 164, 0.45)',
    backgroundColor: 'rgba(61, 214, 164, 0.12)',
    flexShrink: 0,
  },
  personalChipMark: {
    color: colors.accent,
    fontSize: 11,
    fontWeight: '800',
    lineHeight: 13,
  },
  personalChipText: {
    ...typography.caption,
    color: colors.accent,
    fontWeight: '700',
    fontSize: 11,
    lineHeight: 13,
  },
  returnLabel: {
    ...typography.caption,
    color: colors.accentMuted,
  },
  value: {
    ...typography.body,
    color: colors.text,
    fontWeight: '700',
    fontVariant: ['tabular-nums'],
  },
  actions: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: spacing.xs,
  },
  iconBtn: {
    width: 32,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: radii.sm,
    borderWidth: StyleSheet.hairlineWidth,
    borderColor: colors.glassBorder,
    backgroundColor: 'rgba(255,255,255,0.04)',
  },
  deleteBtn: {
    borderColor: 'rgba(248,113,113,0.35)',
  },
  iconBtnPressed: {
    opacity: 0.75,
  },
  iconBtnDisabled: {
    opacity: 0.4,
  },
  iconGlyph: {
    color: colors.textMuted,
    fontSize: 16,
    lineHeight: 18,
    fontWeight: '600',
  },
  deleteText: {
    color: colors.danger,
    fontSize: 20,
    lineHeight: 20,
    fontWeight: '500',
  },
  ctaRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
});
